// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, MoreHorizontal, Pencil, RotateCcw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/api/client";
import {
  type SettingChainEntry,
  useAddSettingForChainMutation,
  useDeleteSettingForChainMutation,
  useEditSettingForChainMutation,
  useSettingChainsQuery,
  useSettingDetailQuery,
} from "@/api/hooks/use-settings";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { ChainLink } from "@/components/shared/chain-link";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { RowId } from "@/components/shared/row-id";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RestoreDefaultDialog } from "@/features/settings/restore-default-dialog";
import { SettingValueCell } from "@/features/settings/setting-value-cell";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { cn } from "@/lib/utils";
import { SettingDialog } from "./setting-dialog";

interface SettingChainsTabProps {
  settingName: string;
  initialFilters?: string;
}

const FILTER_DEFAULTS = {
  search: "",
  onlyOverrides: true,
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "chainId",
  sortDesc: false,
};

export function SettingChainsTab({ settingName, initialFilters }: SettingChainsTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.settingsChainsEdit);
  const { data: setting } = useSettingDetailQuery(settingName);
  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(
    initial.sortId !== "chainId" || initial.sortDesc ? [{ id: initial.sortId, desc: initial.sortDesc }] : [],
  );
  const [search, setSearch] = useState(initial.search);

  const [onlyOverrides, setOnlyOverrides] = useState(initial.onlyOverrides);
  const isFiltered = !!(search || !onlyOverrides);

  const [editEntry, setEditEntry] = useState<SettingChainEntry | null>(null);
  const [restoreEntry, setRestoreEntry] = useState<SettingChainEntry | null>(null);

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        onlyOverrides,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "chainId",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      navigate({
        to: "/settings/$settingName",
        params: { settingName },
        search: { t: "chains", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [search, onlyOverrides, pagination.pageIndex, pagination.pageSize, sorting, navigate, settingName],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const sortBy = (sorting[0]?.id ?? "chainId") as "chainId" | "chainName" | "legibleName" | "value";
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const { data, isLoading, refetch, isFetching } = useSettingChainsQuery({
    settingName,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyOverrides: onlyOverrides || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/settings/{setting_name}/chains", {
      params: {
        path: { setting_name: settingName },
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[onlyOverrides]": onlyOverrides || undefined,
        },
      },
    });
    const items = (res.data as unknown as { settings: SettingChainEntry[] }).settings;
    downloadCsv(
      `setting-${settingName}-chains.csv`,
      ["Chain ID", "Chain", "Value", "Status"],
      items.map((c) => [String(c.chainId), c.legibleName, c.value, c.overrideId ? "Override" : "Default"]),
    );
  }, [settingName, search, sortBy, orderBy, onlyOverrides]);

  const addMutation = useAddSettingForChainMutation();
  const editMutation = useEditSettingForChainMutation();
  const deleteMutation = useDeleteSettingForChainMutation();

  async function onSubmit(value: string) {
    if (!editEntry || !setting) return;
    try {
      if (editEntry.overrideId) {
        await editMutation.mutateAsync({
          chainName: editEntry.chainName,
          body: { overrideId: editEntry.overrideId, newValue: value },
        });
      } else {
        await addMutation.mutateAsync({
          chainName: editEntry.chainName,
          body: { settingId: setting.settingId, newValue: value },
        });
      }
      toast.success(t("settings.list.toast.updated", { name: editEntry.legibleName }));
    } catch {
      // Error toast handled by API middleware
    }
  }

  async function handleRestore() {
    if (!restoreEntry?.overrideId) return;
    try {
      await deleteMutation.mutateAsync({
        chainName: restoreEntry.chainName,
        overrideId: restoreEntry.overrideId,
      });
      toast.success(t("settings.list.toast.restored", { name: restoreEntry.legibleName }));
      setRestoreEntry(null);
    } catch {
      // Error toast handled by API middleware
    }
  }

  const columns = useMemo<DataTableColumnDef<SettingChainEntry>[]>(
    () => [
      {
        accessorKey: "chainId",
        header: t("settings.chains.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("settings.chains.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.chainId} />,
      },
      {
        accessorKey: "legibleName",
        header: t("settings.chains.columns.name"),
        meta: { columnLabel: t("settings.chains.columns.name") },
        cell: ({ row }) => (
          <ChainLink
            chainName={row.original.chainName}
            legibleName={row.original.legibleName}
            className="font-medium"
          />
        ),
      },
      {
        accessorKey: "value",
        header: t("settings.chains.columns.value"),
        meta: { columnLabel: t("settings.chains.columns.value") },
        cell: ({ row }) => (
          <SettingValueCell
            value={row.original.value}
            fieldType={setting?.fieldType ?? "string"}
            name={setting?.name}
          />
        ),
      },
      {
        id: "status",
        header: t("settings.chains.columns.status"),
        enableSorting: false,
        size: 100,
        meta: { columnLabel: t("settings.chains.columns.status") },
        cell: ({ row }) =>
          row.original.overrideId ? (
            <Badge
              variant="secondary"
              className={cn(
                "font-normal bg-[#E3F1FF] border-blue-200 text-foreground dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
              )}
            >
              {t("settings.status.override")}
            </Badge>
          ) : (
            <Badge variant="secondary" className="font-normal">
              {t("settings.status.default")}
            </Badge>
          ),
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) =>
          canEdit ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
                <DropdownMenuItem onSelect={() => setEditEntry(row.original)}>
                  <Pencil className="h-4 w-4" />
                  {t("settings.list.actions.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setRestoreEntry(row.original)} disabled={!row.original.overrideId}>
                  <RotateCcw className="h-4 w-4" />
                  {t("settings.list.actions.restoreDefault")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null,
      },
    ],
    [t, setting?.fieldType, setting?.name, canEdit],
  );

  return (
    <div className="p-6">
      <DataTable
        storageKey="setting-chains"
        columns={columns}
        data={data?.settings ?? []}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={setSearch}
        searchDebounceMs={1000}
        onRefresh={refetch}
        isRefreshing={isFetching}
        onExport={handleExport}
        searchPlaceholder={t("settings.chains.searchPlaceholder")}
        filters={
          <>
            <FilterToggle
              active={onlyOverrides}
              onChange={() => {
                setOnlyOverrides((v) => !v);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              icon={Filter}
              label={t("settings.list.filter.overridesOnly")}
            />
            {isFiltered && !onlyOverrides && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setSearch("");
                  setOnlyOverrides(true);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        }
      />

      {setting && (
        <SettingDialog
          open={!!editEntry}
          onClose={() => setEditEntry(null)}
          settingName={setting.name}
          settingKey={setting.key}
          description={setting.description}
          fieldType={setting.fieldType}
          enumValues={setting.enumValues}
          defaultValue={setting.defaultValue}
          currentValue={editEntry?.value}
          onSave={onSubmit}
        />
      )}

      <RestoreDefaultDialog
        open={!!restoreEntry}
        onClose={() => setRestoreEntry(null)}
        onConfirm={handleRestore}
        settingName={restoreEntry?.legibleName}
        defaultValue={setting?.defaultValue}
        confirmDisabled={!canEdit}
      />
    </div>
  );
}
